import { cn } from '@shared/cove-ui';
import { Facebook, Instagram, LogoSecondary, Youtube } from '../../Icons';
import Collapse from '../../UI/Collapse';
import SubscribeForm from './subscribeForm';
import { FooterLink, headerTranslationKeys, linkHeaders } from './links';
import { useTranslation } from 'react-i18next';

const MobileFooter: React.FC<{
  links: FooterLink;
  onSubscribe: (email: string) => Promise<{ status: string }>;
  isMinimal?: boolean;
  variant?: string;
}> = ({ links, onSubscribe, isMinimal, variant, ...props }) => {
  const { t } = useTranslation('common');

  // Format links so that they are usable by <Collapse /> component
  const footerLinks = linkHeaders
    .map((l, i) => {
      const translationKey = headerTranslationKeys[l];
      const translatedHeader = t(`links.${translationKey}`, {
        defaultValue: l,
      });
      return {
        heading: translatedHeader,
        details: (
          <ul
            key={`footer-link-header-${i}`}
            className="gap-cove-15 mb-cove-15 flex list-none flex-col items-start p-0 text-sm font-extralight"
          >
            {links?.[l]?.map(nl => (
              <li key={nl.label} className="text-sm font-extralight">
                <a href={nl.to} className="no-underline">
                  {nl.label}
                </a>
              </li>
            ))}
          </ul>
        ),
      };
    })
    .filter(l => l);

  return (
    <div
      className={cn(
        'mx-auto my-0 flex w-full max-w-[1256px] flex-col md:hidden',
        variant === 'activate'
          ? 'px-cove-50 py-cove-75'
          : 'px-cove-15 py-cove-50'
      )}
      {...props}
    >
      {isMinimal ? (
        variant === 'activate' ? (
          <div>
            <a
              href="/"
              className="relative flex items-center no-underline"
              aria-label="Go to covesmart.com homepage"
            >
              <LogoSecondary color={'#3ebeb0'} />
            </a>
            <p className="mt-cove-15 text-base text-white md:mt-0">
              Follow us <span className="underline">@covesmart</span>
            </p>
            <div className="gap-cove-15 mt-cove-10 flex justify-start md:justify-end">
              <a
                href="https://www.instagram.com/covesmart/"
                className="no-underline"
              >
                <Instagram
                  color={variant === 'activate' ? '#ffffff' : '#002334'}
                />
                <span className="sr-only">Instagram</span>
              </a>
              <a
                href="https://www.facebook.com/covesmart/"
                className="no-underline"
              >
                <Facebook
                  color={variant === 'activate' ? '#ffffff' : '#002334'}
                />
                <span className="sr-only">Facebook</span>
              </a>
              <a
                href="https://www.youtube.com/channel/UC0i0Rkot3hmtOyBXhvo3PEw?view_as=subscriber"
                className="no-underline"
              >
                <Youtube
                  color={variant === 'activate' ? '#ffffff' : '#002334'}
                />
                <span className="sr-only">Youtube</span>
              </a>
            </div>
            <p className="mt-cove-25 mb-0 text-[12px] text-white">
              © {new Date().getFullYear()} Cove Smart, LLC{' '}
              {t('links.all-rights-reserved', {
                defaultValue: 'All rights reserved.',
              })}{' '}
              14015 Minuteman Drive, Draper, UT 84020
            </p>
          </div>
        ) : (
          <div className="sm:py-cove-50 md:px-cove-87 md:py-cove-50 gap-cove-25 m-0 flex flex-col-reverse justify-between p-0 md:flex-row md:gap-0">
            <div className="gap-cove-15 flex flex-wrap items-center text-sm font-light md:flex-nowrap">
              <a
                href="/"
                className="flex items-center no-underline"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <LogoSecondary color="#002334" />
                <span className="sr-only">
                  Go back to covesmart.com homepage
                </span>
              </a>
              <div>
                <p className="m-0 text-sm font-extralight">
                  © {new Date().getFullYear()} Cove Smart, LLC{' '}
                  {t('links.all-rights-reserved', {
                    defaultValue: 'All rights reserved.',
                  })}
                </p>
                <p className="m-0 text-sm font-extralight">
                  14015 Minuteman Drive, Draper, UT 84020
                </p>
              </div>
            </div>
            <div>
              <SubscribeForm
                handleSubscribe={onSubscribe}
                style={{ marginTop: 0 }}
              />
            </div>
          </div>
        )
      ) : (
        <>
          <Collapse
            id="footer-mobile-links"
            items={footerLinks}
            className="bg-cove-dove border-b-cove-navy-blue border-b [&>div]:text-base sm:[&>div]:text-lg"
          />
          <SubscribeForm handleSubscribe={onSubscribe} />
          <div className="md:pt-cove-35 pt-cove-25 md:mt-cove-50 md:border-t-cove-navy-blue gap-cove-25 border-top-0 m-0 flex flex-col-reverse justify-between md:flex-row md:gap-0 md:border-t">
            <div className="gap-cove-15 flex flex-wrap items-center text-sm font-light md:flex-nowrap">
              <a href="/legal/" className="no-underline">
                Legal
              </a>
              <a href="/privacy-policy/" className="no-underline">
                Privacy
              </a>
              <a href="/state-licensing/" className="no-underline">
                State Licenses
              </a>
              <p className="text-sm font-extralight">
                © {new Date().getFullYear()} Cove Smart, LLC{' '}
                {t('links.all-rights-reserved', {
                  defaultValue: 'All rights reserved.',
                })}{' '}
                14015 Minuteman Drive, Draper, UT 84020
              </p>
            </div>
            <div className="gap-cove-15 flex flex-wrap items-center text-sm font-light md:flex-nowrap">
              <a
                href="https://www.instagram.com/covesmart/"
                className="no-underline"
              >
                <Instagram color="#002334" />
                <span className="sr-only">Instagram</span>
              </a>
              <a
                href="https://www.facebook.com/covesmart/"
                className="no-underline"
              >
                <Facebook color="#002334" />
                <span className="sr-only">Facebook</span>
              </a>
              <a
                href="https://www.youtube.com/channel/UC0i0Rkot3hmtOyBXhvo3PEw?view_as=subscriber"
                className="no-underline"
              >
                <Youtube color="#002334" />
                <span className="sr-only">Youtube</span>
              </a>
            </div>
          </div>
        </>
      )}
      {/* <p className="mt-cove-25 text-sm">
        If your purchase includes sale items, you will be given the amount of
        entries matching the sale price. NO PURCHASE NECESSARY. Ends 12/2/25.
        Must be: i) a legal US resident of the 50 US or DC and age of majority
        or ii) a legal resident of Canada (excl. Quebec) and age of majority at
        time of entry. Subject to Official Rules available{' '}
        <a href="https://sharedsweeps.com/rules/" className="underline">
          here
        </a>
        . Void where prohibited. Sponsored By Shared Sweeps.
      </p> */}
    </div>
  );
};

export default MobileFooter;
